import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import "./HeroImage.css";
import "../../containers/Landing/LandingPage.css";
import hero251 from "../../assets/images/Hero251.png";
import hero252 from "../../assets/images/Hero252.png";
import hero253 from "../../assets/images/Hero253.png";
import hero254 from "../../assets/images/Hero254.png";
import hero255 from "../../assets/images/Hero255.png";
import landingCardBg from "../../assets/images/HeroBG.png";
import insta from "../../assets/images/InstagramHero.png";
import snap from "../../assets/images/SnapchatHero.png";
import percent from "../../assets/images/PercentHero.png";
import present from "../../assets/images/PresentHero.png";
import tiktok from "../../assets/images/TiktokHero.png";
import speaker from "../../assets/images/SpeakerHero.png";
import { useResponsive } from "./hooks/useResponsive";
import { shouldUseReducedMedia } from "../../utils/browser";
import HeroLottie from "./HeroLottie";
import FounderCtaLink from "./FounderCtaLink";

const heroImages = [hero251, hero252, hero253, hero254, hero255];

const HeroImage = ({ lightMode }) => {
  const { isLgUp, isMobile } = useResponsive();
  const [currentImage, setCurrentImage] = useState(0);
  const [isNarrowMobile, setIsNarrowMobile] = useState(false);
  const reduceMotion = shouldUseReducedMedia();

  useEffect(() => {
    const query = "(max-width: 399px)";
    const mql = window.matchMedia(query);
    const update = () => setIsNarrowMobile(mql.matches);
    update();
    mql.addEventListener?.("change", update);
    mql.addListener?.(update);
    return () => {
      mql.removeEventListener?.("change", update);
      mql.removeListener?.(update);
    };
  }, []);

  useEffect(() => {
    if (reduceMotion || heroImages.length <= 1) return undefined;

    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [reduceMotion]);

  const ctaClassName = `hero-founder-cta${
    lightMode ? " hero-founder-cta--light" : ""
  }`;

  const desktopImage = reduceMotion ? (
    <img
      className="m-0 p-0"
      src={heroImages[0]}
      alt=""
      loading="eager"
      decoding="async"
      style={{
        position: "absolute",
        bottom: 0,
        left: "50%",
        transform: "translateX(-50%)",
        maxWidth: isMobile ? "80%" : "60%",
        maxHeight: isMobile ? "80%" : "50%",
        width: "auto",
        height: "auto",
        zIndex: 2,
      }}
    />
  ) : (
    <motion.img
      key={currentImage}
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 1, 1, 1, 0] }}
      transition={{
        duration: 5,
        ease: "easeInOut",
        times: [0, 0.2, 0.4, 0.7, 1],
        repeat: Infinity,
        repeatType: "loop",
      }}
      className="m-0 p-0"
      src={heroImages[currentImage]}
      alt=""
      loading="eager"
      decoding="async"
      style={{
        position: "absolute",
        bottom: 0,
        left: "50%",
        transform: "translateX(-50%)",
        maxWidth: isMobile ? "80%" : "60%",
        maxHeight: isMobile ? "80%" : "50%",
        width: "auto",
        height: "auto",
        zIndex: 2,
      }}
    />
  );

  return (
    <>
      {/* Desktop */}
      <div
        className={`d-none d-md-flex align-items-center mx-auto lp-card${
          lightMode ? " lp-light" : ""
        }`}
        style={{
          borderRadius: "40px",
          minHeight: "90vh",
          width: "95vw",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          backgroundImage: `url(${landingCardBg})`,
          backgroundSize: "cover",
          backgroundPosition: "50% 0%",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="hero-sect d-flex flex-column align-items-center"
          style={{
            paddingBottom: isLgUp ? "0rem" : "20rem",
            marginTop: isLgUp ? "6rem" : "0rem",
          }}
        >
          <div
            className={`text-center mb-3 lp-heading${
              lightMode ? " lp-light" : ""
            }`}
            style={{
              lineHeight: "1",
              letterSpacing: "-0.05em",
              zIndex: 2,
            }}
          >
            <span style={{ color: lightMode ? "black" : "white" }}>
              Tap into the{" "}
              <span style={{ color: "rgba(0, 255, 209, 1)" }}>
                social network
              </span>
            </span>
            <br />
            <span style={{ color: lightMode ? "black" : "white" }}>
              of your{" "}
              <span style={{ color: "rgba(0, 255, 209, 1)" }}>attendees</span>
            </span>
          </div>
          <p
            className="text-center lp-subheading"
            style={{
              letterSpacing: "-0.04em",
              lineHeight: "100%",
              marginBottom: ".25rem",
            }}
          >
            Say goodbye to empty venues and hidden fees, and hello to <br /> to
            packed events driven by your own audience
          </p>
          <div
            className={`mt-1 w-100 d-flex justify-content-center align-items-center lp-btn-group${
              isLgUp ? "" : "-mobile"
            }`}
            style={{ zIndex: 99 }}
          >
            <FounderCtaLink
              className={`col-auto my-3 text-center ${ctaClassName}`}
            />
          </div>
          {desktopImage}
        </div>
      </div>

      {/* Mobile */}
      <motion.div
        className={`d-md-none ${lightMode ? " lp-light" : ""}`}
        style={{
          borderRadius: "50px",
          padding: "1rem .5rem",
          position: "relative",
        }}
        initial={reduceMotion ? false : { y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{
          duration: reduceMotion ? 0 : 1.2,
          ease: [0.25, 0.46, 0.45, 0.94],
          type: "tween",
        }}
      >
        <div
          className="d-md-none d-flex justify-content-center align-items-center"
          style={{
            width: "114%",
            aspectRatio: "1 / 1",
            top: "1%",
            left: "-7%",
            alignSelf: "center",
            borderWidth: "1px",
            borderRadius: "50%",
            position: "absolute",
            border: lightMode
              ? "1px solid rgba(38, 38, 38, 0.15)"
              : "1px solid rgba(255,255,255,.15)",
          }}
        >
          <img
            src={insta}
            alt=""
            style={{
              position: "absolute",
              top: "4.079%",
              left: "18.356%",
              width: "8.770%",
              aspectRatio: "43 / 41",
            }}
          />
          <img
            src={snap}
            alt=""
            style={{
              position: "absolute",
              top: "0px",
              left: "61.187%",
              width: "6.527%",
              aspectRatio: "32/29",
            }}
          />
          <img
            src={percent}
            alt=""
            style={{
              position: "absolute",
              top: "5.099%",
              left: "75.464%",
              width: "13.257%",
              aspectRatio: "1 / 1",
            }}
          />
          <div
            className="d-md-none text-center mx-auto"
            style={{
              width: "80%",
              aspectRatio: "1 / 1",
              alignSelf: "center",
              borderWidth: "1px",
              borderRadius: "50%",
              position: "absolute",
              border: lightMode
                ? "1px solid rgba(38, 38, 38, 0.15)"
                : "1px solid rgba(255,255,255,.15)",
            }}
          >
            <img
              src={present}
              alt=""
              style={{
                position: "absolute",
                top: "13.842%",
                left: "0",
                width: "16.334%",
                aspectRatio: "1 / 1",
              }}
            />
            <img
              src={tiktok}
              alt=""
              style={{
                position: "absolute",
                top: "-4.43%",
                left: "36%",
                width: "11.074%",
                aspectRatio: "40 / 37",
              }}
            />
            <img
              src={speaker}
              alt=""
              style={{
                position: "absolute",
                top: "13.842%",
                left: "84.44%",
                width: "15.227%",
                aspectRatio: "1 / 1",
              }}
            />
          </div>
        </div>
        <p
          className={`text-center lp-heading${lightMode ? " lp-light" : ""}`}
          style={{
            fontSize: "36px",
            lineHeight: "36px",
            fontWeight: 600,
            marginTop: isNarrowMobile ? "4.5rem" : "5.5rem",
          }}
        >
          Tap into the <br />
          <span style={{ color: lightMode ? "#13B497" : "#2EEBC9" }}>
            social network
          </span>
          <br />
          of your{" "}
          <span style={{ color: lightMode ? "#13B497" : "#2EEBC9" }}>
            attendees
          </span>
        </p>
        <p
          className={`px-2 text-center my-2 lp-subheading${
            lightMode ? " lp-light" : ""
          }`}
          style={{
            fontSize: "13px",
            fontWeight: 500,
            letterSpacing: "-0.04em",
            lineHeight: "100%",
            marginBottom: ".25rem",
          }}
        >
          Say goodbye to empty venues and hidden fees, and <br /> hello to
          packed events driven by your own audience!
        </p>
        <div
          className={isNarrowMobile ? "py-3" : "py-5"}
          style={{ width: "100%" }}
        >
          <HeroLottie />
        </div>
        <div
          className="d-flex flex-column align-items-center px-3"
          style={{
            width: "100%",
            margin: "0 auto",
            position: "relative",
            zIndex: 99,
          }}
        >
          <FounderCtaLink
            className={`w-100 my-2 text-center ${ctaClassName}`}
          />
        </div>
      </motion.div>
    </>
  );
};

export default HeroImage;
